import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { AccessRestrictedDialogComponent } from '../access';
import { UserBulkActionsService } from './user-bulk-actions/user-bulk-actions.service';

import { UserService as BusinessCenterUserService } from '@vendasta/business-center';
import {
  Button,
  ButtonCell,
  MenuCell,
  MenuItem,
  SortDescriptor,
  SortOrder,
  SwitchCell,
  TableViewModel,
} from '@vendasta/uikit';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { filter, first, startWith, take, tap } from 'rxjs/operators';

import { newPartnerServiceContext, SSOService } from '@vendasta/sso';
import { AppConfigService } from '../app-config.service';
import { VaDialogService } from '../common/va-dialog.service';
import { AccessService, Feature, StubAccessService } from '../core/access';
import { getImageSrc } from '../core/images';
import { PartnerService } from '../core/partner.service';
import { User } from '../core/user';
import {
  BusinessCenterTab,
  businessCenterTabList,
  EXECUTIVE_REPORT_TAB,
  GUIDES_TAB,
  RECOMMENDATIONS_TAB,
} from '../user-permissions/account-group-permissions/business-center-tab';
import { VConfigService } from '../vconfig/vconfig.service';
import { ResendWelcomeEmailDialogComponent } from './resend-welcome-email-dialog/resend-welcome-email-dialog.component';
import { UserService } from './user.service';

@Component({
  templateUrl: './manage-users.component.html',
  styleUrls: ['./manage-users.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageUsersComponent implements OnInit, OnDestroy {
  constructor(
    private dialogService: VaDialogService,
    private snackbarService: SnackbarService,
    public usersService: UserService,
    public partnerService: PartnerService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private vconfigService: VConfigService,
    private userBulkActionsService: UserBulkActionsService,
    @Inject(StubAccessService) private accessService: AccessService,
    private partner: PartnerService,
    private dialog: MatDialog,
    private bcUserService: BusinessCenterUserService,
    private ssoService: SSOService,
    @Inject('PARTNER_ID') public readonly partnerId$: Observable<string>,
    @Inject('USER_ID') public readonly userId$: Observable<string>,
    private appConfig: AppConfigService,
  ) {}

  private searchTerm$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  readonly searchTerm$: Observable<string> = this.searchTerm$$.asObservable();

  users$: Observable<User[]>;
  busy$: Observable<boolean>;
  hasAccessToLists: boolean;
  loginUrl: string;
  loginUrlText: string;
  subscriptions: Subscription[] = [];

  totalUserCount = 0;
  displayBulkActions = false;
  hideEmptyState = false;
  selectedUsers: User[] = [];

  getImageSrc = getImageSrc;

  private buttonTextDisplay$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  readonly buttonTextDisplay$: Observable<string> = this.buttonTextDisplay$$.asObservable();

  initialSort: SortDescriptor = {
    fieldname: 'first_name',
    order: SortOrder.Ascending,
  };

  isSearching = false;
  searchedUsers: User[] = [];
  tabsDetails$: Observable<any>;
  tabs: BusinessCenterTab[];

  private accessRestrictedDialogRef: MatDialogRef<AccessRestrictedDialogComponent>;

  usersTable = new TableViewModel<User>({
    columns: [
      {
        title: 'Name',
        cellForItem: (user) => user.fullName,
        sortOn: 'first_name',
        config: { width: '140px' },
      },
      {
        title: 'Email',
        cellForItem: (user) => user.email,
        sortOn: 'email',
        config: { width: '316px' },
      },
      // {
      //   title: 'Days Active *',
      //   cellForItem: (user) => (user.daysActive || 0).toString(),
      //   config: { width: '100px' },
      // },
      {
        title: 'Accounts',
        cellForItem: (user) => (user.accountGroupAssociationCount || 0).toString(),
        config: { width: '100px' },
      },
      {
        title: 'Notifications',
        cellForItem: (user) => this.toggleCell(user),
        alignment: 'left',
        config: { width: '90px' },
      },
      {
        title: '',
        cellForItem: (user) => this.actionsCell(user),
        alignment: 'right',
        config: { width: '60px' },
      },
      {
        title: '',
        cellForItem: (user) => this.actionsMenu(user),
        alignment: 'right',
        config: { width: '44px' },
      },
    ],
    selection: {
      selectable: true,
      selectAll: true,
      selectionChanged: (selected) => {
        this.selectedUsers = selected;
        this.updateButtonCount();
      },
    },
    loadMoreItemsTriggered: () => this.onLoadMore(),
  });

  ngOnInit(): void {
    const userEmailKey = 'email';
    const userParam = this.activatedRoute.snapshot.queryParams[userEmailKey] || '';
    this.searchTerm$$.next(userParam);

    this.busy$ = this.usersService.isLoading$;
    this.users$ = this.usersService
      .search(
        this.searchTerm$.pipe(
          startWith(userParam),
          tap((searchTerm) => {
            this.isSearching = !!searchTerm;
          }),
        ),
      )
      .pipe(
        tap((users) => {
          if (this.isSearching) {
            this.searchedUsers = users;
          } else {
            this.searchedUsers = [];
          }
          this.hideEmptyState = this.hideEmptyState || users?.length > 0;
          this.updateButtonCount();
        }),
      );

    this.partnerService.getBusinessCenterHostname().subscribe((loginUrl) => {
      this.loginUrlText = loginUrl;
      this.loginUrl = loginUrl + '/login/';
    });

    this.checkTabsVisible();

    this.subscriptions.push(
      this.accessService.hasAccessToFeature(Feature.lists).subscribe((hasAccess) => {
        this.hasAccessToLists = hasAccess;
      }),
    );
  }

  ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
    this.usersService.clearUserSubjects();
  }

  private checkTabsVisible(): void {
    const whitelabelConfig$ = this.vconfigService.getConfigurations(this.partnerService.partnerId, [
      'vbc_show_content_library',
    ]);
    const hasAccessToGuides$ = this.accessService.hasAccessToFeature(Feature.contentLibrary);
    const hasAccessToLocalMarketingIndex$ = this.accessService.hasAccessToFeature(Feature.localMarketingIndex);
    const hasAccessToExecutiveReport$ = this.accessService.hasAccessToFeature(Feature.executiveReport);

    this.tabsDetails$ = combineLatest([
      whitelabelConfig$,
      hasAccessToGuides$,
      hasAccessToLocalMarketingIndex$,
      hasAccessToExecutiveReport$,
    ]).pipe(
      first(),
      tap(([wlConfig, hasAccessToGuides, hasAccessToLocalMarketingIndex, hasAccessToExecutiveReport]) => {
        const exclude = [];
        if (!wlConfig.vbc_show_content_library) {
          exclude.push(RECOMMENDATIONS_TAB);
          exclude.push(GUIDES_TAB);
        }
        if (!hasAccessToGuides) {
          exclude.push(GUIDES_TAB);
        }
        if (!hasAccessToExecutiveReport) {
          exclude.push(EXECUTIVE_REPORT_TAB);
        }
        if (!hasAccessToLocalMarketingIndex) {
          exclude.push(RECOMMENDATIONS_TAB);
        }
        // TODO: Remove this when we are wanting to release reccomendations to everyone. Currently only ABC can view it.
        if (this.partnerService.partnerId !== 'ABC') {
          exclude.push(RECOMMENDATIONS_TAB);
        }
        this.tabs = businessCenterTabList.filter((tab) => exclude.indexOf(tab.id) === -1);
      }),
    );
  }

  updateSearchTerm(newTerm: string): void {
    this.searchTerm$$.next(newTerm.toLowerCase());
  }

  navigateToBulkActions(): void {
    let users = this.selectedUsers;
    let allUsersAvailable = true;
    if (this.isSearching && this.selectedUsers.length === 0) {
      users = this.searchedUsers;
      allUsersAvailable = false;
    }

    this.userBulkActionsService.setSelectedUsersInfo(
      users,
      this.selectedUsers.length === 0 && allUsersAvailable,
      this.totalUserCount,
    );
    this.userBulkActionsService.setAvailableTabs(this.tabs);
    this.router.navigate(['/bc-admin/users/bulk-update']);
  }

  public createUserClick(): void {
    this.router.navigateByUrl('/bc-admin/user/create/');
  }

  private updateButtonCount(): void {
    if (this.selectedUsers.length === 0 && this.searchedUsers.length === 0 && !this.isSearching) {
      this.totalUserCount = this.usersService.totalResults;
      this.displayBulkActions = this.usersService.totalResults >= 1;
      this.buttonTextDisplay$$.next(
        `Bulk Update ${this.usersService.totalResults} User${this.usersService.totalResults === 1 ? '' : 's'}`,
      );
    } else if (this.selectedUsers.length > 0) {
      this.displayBulkActions = this.selectedUsers.length >= 1;
      this.buttonTextDisplay$$.next(
        `Bulk Update ${this.selectedUsers.length} User${this.selectedUsers.length === 1 ? '' : 's'}`,
      );
    } else if (this.searchedUsers.length > 0 && this.isSearching) {
      this.displayBulkActions = this.searchedUsers.length >= 1;
      this.buttonTextDisplay$$.next(
        `Bulk Update ${this.searchedUsers.length} User${this.searchedUsers.length === 1 ? '' : 's'}`,
      );
    }
  }

  toggleCell(user: User): SwitchCell {
    return new SwitchCell({
      disabled: false,
      checked: user.notificationsEnabled,
      onClick: (checked) => this.handleNotificationsToggle(user, checked),
    });
  }

  actionsCell(user: User): ButtonCell {
    return new ButtonCell(this.impersonateButton(user));
  }

  deleteCell(user: User): ButtonCell {
    return new ButtonCell({
      type: 'icon',
      icon: 'close',
      onClick: () => this.deleteUser(user),
    });
  }

  impersonateButton(user: User): Button {
    return {
      type: 'icon',
      icon: 'people',
      color: 'primary',
      onClick: () => {
        this.ssoService
          .getEntryUrl('VBC', newPartnerServiceContext(this.partnerService.partnerId), {
            impersonation: user.unifiedUserId,
          })
          .subscribe({
            next: (url) => {
              if (url === undefined || url === 'undefined') {
                this.snackbarService.errorSnack('Error Impersonating User');
                return;
              }
              window.open(url, '_blank', 'noopener');
            },
            error: () => this.snackbarService.errorSnack('Error Impersonating User'),
          });
      },
      tooltip: 'Impersonate',
    };
  }

  actionsMenu(user: User): MenuCell {
    const menuItems: MenuItem[] = [
      {
        text: 'Edit User',
        leftIcon: 'edit',
        link: { url: `/bc-admin/user/${user.userId}/edit/` },
      },
      {
        text: 'Edit Permissions',
        leftIcon: 'vpn_key',
        link: { url: `/bc-admin/users/${user.userId}/permissions/` },
      },
      {
        text: 'Edit Notifications',
        leftIcon: 'notifications',
        link: { url: `/bc-admin/users/${user.userId}/notifications/` },
      },
      {
        text: 'Resend Welcome Email',
        leftIcon: 'email',
        click: () => this.presentSendWelcomeEmailDialog(user),
      },
      {
        text: 'Delete User',
        showTopDivider: true,
        leftIcon: 'close',
        click: () => this.deleteUser(user),
      },
    ];

    return new MenuCell({ menuItems: menuItems });
  }

  presentSendWelcomeEmailDialog(user: User): void {
    this.dialog.open(ResendWelcomeEmailDialogComponent, {
      data: {
        userID: user.unifiedUserId,
        partnerID: this.partnerService.partnerId,
        loginUrl: this.loginUrl,
      },
      width: '600px',
    });
  }

  onLoadMore(): void {
    this.usersService.loadMore();
  }

  onSort(event: SortDescriptor): void {
    this.usersService.setSort(event.fieldname, event.order);
  }

  deleteUser(user: User): void {
    this.subscriptions.push(
      this.dialogService
        .delete('Delete', `Are you sure you want to delete user ${user.email}?`, 'Delete')
        .pipe(
          filter((result) => result === 'yes'),
          take(1),
        )
        .subscribe(() => {
          this.usersService.delete(user);
        }),
    );
  }

  navigateToURL(url: string): void {
    this.router.navigateByUrl(url);
  }

  private handleNotificationsToggle(user: User, checked: boolean): void {
    let accountOrAccounts = '';
    if (user.accountGroupAssociationCount === 1) {
      accountOrAccounts = 'account';
    } else {
      accountOrAccounts = 'accounts';
    }

    let message = '';
    let buttonText = '';
    if (checked) {
      message = `<strong>${user.fullName || user.email}</strong> will receive instant notifications,
                       daily digests, Task Manager reports, and Executive reports for the <strong>${
                         user.accountGroupAssociationCount
                       }
                       ${accountOrAccounts}</strong> they have permission to (based on their existing email
                       preferences). This will not affect their email campaigns.`;
      buttonText = 'Turn On';
    } else {
      message = `<strong>${user.fullName || user.email}</strong> will no longer receive instant notifications,
                       daily digests, Task Manager reports, or Executive reports for the <strong>${
                         user.accountGroupAssociationCount
                       }
                       ${accountOrAccounts}</strong> they have permission to. However, they may continue to receive
                       email campaigns.`;
      buttonText = 'Turn Off';
    }

    this.subscriptions.push(
      this.dialogService
        .confirm('Are you sure?', message, buttonText)
        .pipe(take(1))
        .subscribe((dialogResponse) => {
          if (dialogResponse === 'yes') {
            this.usersService.toggleNotificationsEnabled(user, checked).subscribe({
              next: () => {
                user.notificationsEnabled = checked;
                this.snackbarService.successSnack('Notifications updated successfully.');
              },
              error: () => {
                this.snackbarService.errorSnack('Notifications update failed. Try again.');
                this.usersTable.updateColumnTitled('Notifications', {
                  cellForItem: this.toggleCell.bind(this),
                });
              },
            });
          } else {
            this.usersTable.updateColumnTitled('Notifications', {
              cellForItem: this.toggleCell.bind(this),
            });
          }
        }),
    );
  }

  private openBulkUserImport(): void {
    this.router.navigate(['/bc-admin/users/bulk-import']);
  }

  public importUsersCallback(hasAccess: boolean): void {
    return hasAccess ? this.openBulkUserImport() : this.openAccessRestrictedDialog(Feature.lists);
  }

  private openAccessRestrictedDialog(featureId: string): void {
    this.dialog.closeAll();
    this.accessRestrictedDialogRef = this.dialog.open(AccessRestrictedDialogComponent, {
      width: '710px',
      data: { featureId: featureId },
    });
    this.subscriptions.push(
      this.accessRestrictedDialogRef.afterClosed().subscribe(() => (this.accessRestrictedDialogRef = null)),
    );
  }
}
